/**
 * Reference clips: the old procedural melee swing, instant-spell thrust and cast / channel poses,
 * rebuilt in the clip format (with anticipation, a snappy strike, an impact hold, body motion and
 * class-coloured trails). Any ability without its own clip plays these, so every hero action
 * animates.
 */
import { type Clip, type ColorDef, type TrailStyle, defineClip } from './clips';
import { type ClassClips, registerClassDefaults, registerGenericDefaults } from './registry';

export interface ClassLook {
  /** Trail head colour of swings / spells. */
  color: ColorDef;
  tail?: ColorDef;
  swingStyle: TrailStyle;
  spellStyle: TrailStyle;
  /** Swing with both hands (dual wield): the off-hand follows through. */
  dual?: boolean;
}

export const CLASS_LOOK: Record<string, ClassLook> = {
  warden: { color: '#ffcf73', tail: '#8a5a12', swingStyle: 'crescent', spellStyle: 'holy' },
  blademaster: { color: '#ff3b2f', tail: '#6e0606', swingStyle: 'crescent', spellStyle: 'streak', dual: true },
  lightbinder: { color: '#ffe7a3', tail: '#b38a2e', swingStyle: 'holy', spellStyle: 'holy' },
  stormcaller: { color: '#86ccff', tail: '#2a4fc4', swingStyle: 'electric', spellStyle: 'electric' },
};
const NEUTRAL_LOOK: ClassLook = { color: '#e8e2d6', swingStyle: 'crescent', spellStyle: 'streak' };

/** Diagonal overhead slash: wind up (twist right, crouch), snap down across the body with a lead step, brief hold, recover. */
export function makeSwing(id: string, look: ClassLook): Clip {
  return defineClip({
    id,
    duration: 0.52,
    keys: [
      {
        t: 0.15,
        ease: 'out',
        pose: {
          spine: [-0.06, -0.38, 0],
          chest: [0, -0.15, 0],
          head: [0, 0.25, 0],
          rightUpperArm: [2.25, -0.15, 0.8],
          rightLowerArm: [0.55],
          ...(look.dual ? { leftUpperArm: [0.55, 0.1, 0.45], leftLowerArm: [0.7] } : {}),
          leftUpperLeg: [0.12],
          leftLowerLeg: [0.25],
          rightLowerLeg: [0.22],
        },
        root: { lunge: -0.06 },
      },
      {
        t: 0.23,
        ease: 'snap',
        hold: 0.05,
        pose: {
          spine: [0.22, 0.42, 0],
          chest: [0.06, 0.2, 0],
          head: [-0.08, -0.35, 0],
          rightUpperArm: [0.7, 0.95, 0.2],
          rightLowerArm: [0.08],
          ...(look.dual ? { leftUpperArm: [1.5, 0.7, 0.25], leftLowerArm: [0.25] } : {}),
          leftUpperLeg: [0.45],
          leftLowerLeg: [0.35],
          rightUpperLeg: [-0.2],
          rightLowerLeg: [0.3],
        },
        root: { lunge: 0.24 },
      },
      { t: 0.52, ease: 'out' },
    ],
    trails: [
      { from: 0.13, to: 0.3, hand: 'right', color: look.color, tail: look.tail, style: look.swingStyle, width: 0.8, fade: 0.18 },
      ...(look.dual ? [{ from: 0.17, to: 0.32, hand: 'left' as const, color: look.color, tail: look.tail, style: look.swingStyle, width: 0.75, fade: 0.16, alpha: 0.55 }] : []),
    ],
    cues: [{ t: 0.23, fx: 'sparks', at: 'tip', scale: 0.6 }],
  });
}

/** Instant spell: pull the hand back by the shoulder, then thrust it out (a small lunge), hold, recover. */
export function makeThrust(id: string, look: ClassLook): Clip {
  return defineClip({
    id,
    duration: 0.42,
    keys: [
      { t: 0.09, ease: 'out', pose: { spine: [-0.05, -0.25, 0], rightUpperArm: [0.1, 0.3, 0.6], rightLowerArm: [1.1] } },
      {
        t: 0.17,
        ease: 'snap',
        hold: 0.07,
        pose: { spine: [0.1, 0.2, 0], chest: [0.05, 0.1, 0], rightUpperArm: [0.45, 1.3, 1.05], rightLowerArm: [-0.1], leftUpperLeg: [0.2], leftLowerLeg: [0.2] },
        root: { lunge: 0.12 },
      },
      { t: 0.42, ease: 'out' },
    ],
    trails: [{ from: 0.08, to: 0.24, hand: 'right', color: look.color, tail: look.tail, style: look.spellStyle, width: 0.6, fade: 0.16, alpha: 0.6 }],
    cues: [{ t: 0.17, fx: 'flash', at: 'hand', scale: 0.5 }],
  });
}

const CAST_POSE = {
  leftUpperArm: [0.3, 1.05, 0.9],
  leftLowerArm: [0.22],
  rightUpperArm: [0.45, 1.15, 0.95],
  rightLowerArm: [0.12],
  spine: [0.04, 0, 0],
} as const;

/** Cast bar: both hands forward and up, a slow breathing sway (loops). */
export function makeCast(id: string): Clip {
  return defineClip({
    id,
    duration: 1.2,
    loop: true,
    loopFrom: 0.2,
    blendIn: 0.12,
    keys: [
      { t: 0.2, ease: 'out', pose: CAST_POSE },
      { t: 0.7, pose: { ...CAST_POSE, chest: [-0.03, 0, 0], leftUpperArm: [0.36, 1.08, 0.95], rightUpperArm: [0.5, 1.12, 1.0] } },
    ],
  });
}

/** Channel: the cast pose with the hands working against each other (loops). */
export function makeChannel(id: string): Clip {
  return defineClip({
    id,
    duration: 1.1,
    loop: true,
    loopFrom: 0.2,
    blendIn: 0.12,
    keys: [
      { t: 0.2, ease: 'out', pose: CAST_POSE },
      { t: 0.425, pose: { ...CAST_POSE, leftUpperArm: [0.3, 1.11, 0.9], rightUpperArm: [0.45, 1.09, 0.95] } },
      { t: 0.65, pose: CAST_POSE },
      { t: 0.875, pose: { ...CAST_POSE, leftUpperArm: [0.3, 0.99, 0.9], rightUpperArm: [0.45, 1.21, 0.95] } },
    ],
  });
}

let done = false;

/** Register the generic and per-class defaults (idempotent). */
export function registerDefaultClips(): void {
  if (done) return;
  done = true;
  registerGenericDefaults({
    swing: makeSwing('default.swing', NEUTRAL_LOOK),
    spell: makeThrust('default.spell', NEUTRAL_LOOK),
    cast: makeCast('default.cast'),
    channel: makeChannel('default.channel'),
  });
  for (const [cls, look] of Object.entries(CLASS_LOOK)) {
    const c: ClassClips = { swing: makeSwing(`${cls}.swing`, look), spell: makeThrust(`${cls}.spell`, look) };
    registerClassDefaults(cls, c);
  }
}
